import React, { useState, useContext, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { DashboardContext } from '@/context/DashboardContext'
import toast from 'react-hot-toast'
import { Pencil, Trash2, ExternalLink, Twitter, BookOpen, PersonStanding, Search } from 'lucide-react'
import { useWorkspace } from '@/context/WorkspaceContext'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { X } from 'lucide-react'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import { MultiSelect } from '@/components/ui/multi-select'
import { uploadLogo } from '@/utils/uploadCompetitorsLogo'
import { Progress } from '@/components/ui/progress'
import { isValidUrl } from '@/utils'

interface Competitor {
  id: string | undefined
  name: string
  website_url: string
  threat_level: number
  similarity: number
  segment: string
  blog_url: string
  twitter_url: string
  product_id: string
  description: string
  logo_url: string
}

// interface ProductSegment {
//   segment_id: number
//   segment_name: string
// }

// EmptyState component for when no competitors are found
const EmptyState = ({ onSearchClick, isSearching, onAddClick }) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 border rounded-lg bg-white dark:bg-gray-800 shadow-sm mt-8 h-[80vh] relative overflow-hidden">
      {/* Radar scan animation */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]">
          <div className="absolute inset-0 rounded-full border-4 border-[#FF4A8E]/20 animate-[radar_4s_ease-in-out_infinite]" />
          <div className="absolute inset-0 rounded-full border-4 border-[#FF4A8E]/20 animate-[radar_4s_ease-in-out_infinite_1s]" />
          <div className="absolute inset-0 rounded-full border-4 border-[#FF4A8E]/20 animate-[radar_4s_ease-in-out_infinite_2s]" />
        </div>
      </div>

      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6 relative z-10">
        <svg className="w-8 h-8 text-[#FF4A8E] dark:text-[#FF4A8E]" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.2963 3.64726C11.014 3.35645 10.8555 2.96201 10.8555 2.55073C10.8555 2.13945 11.014 1.74502 11.2963 1.4542C11.5785 1.16338 11.9614 1 12.3605 1C12.7597 1 13.1425 1.16338 13.4247 1.4542C13.707 1.74502 13.8656 2.13945 13.8656 2.55073C13.8656 2.96201 13.707 3.35645 13.4247 3.64726C13.1425 3.93808 12.7597 4.10146 12.3605 4.10146C11.9614 4.10146 11.5785 3.93808 11.2963 3.64726ZM9.19507 13.9158C9.42434 13.5708 9.33054 13.1053 8.98556 12.876C8.64059 12.6468 8.17507 12.7406 7.9458 13.0855L6.58759 15.1292L1.28065 13.2919C0.889226 13.1564 0.462064 13.3639 0.326552 13.7553C0.191041 14.1467 0.398496 14.5739 0.789916 14.7094L6.6455 16.7366C6.96853 16.8485 7.3263 16.7277 7.5155 16.443L9.19507 13.9158ZM9.85289 6.49702L8.79687 11.6112C8.72372 11.9655 8.91387 12.3215 9.249 12.4577L14.1014 14.4297L12.8238 18.2625C12.6928 18.6554 12.9052 19.0802 13.2981 19.2111C13.6911 19.3421 14.1158 19.1298 14.2468 18.7368L15.7468 14.2368C15.8719 13.8616 15.6841 13.4537 15.3177 13.3048L10.3921 11.3031L11.2521 7.13862L13.4716 10.0818C13.6531 10.3224 13.9609 10.431 14.2532 10.3576L18.7532 9.22703C19.1549 9.1261 19.3988 8.71862 19.2978 8.31689C19.1969 7.91516 18.7894 7.67131 18.3877 7.77224L14.3699 8.78165L11.4526 4.91328C11.3163 4.73256 11.1097 4.62724 10.8929 4.61586C10.7859 4.60995 10.6814 4.62724 10.5854 4.66394L4.32917 6.7895C4.1343 6.85571 3.9752 6.99908 3.88915 7.18604C3.80309 7.37299 3.79764 7.58709 3.87408 7.77818L4.87408 10.2782C5.02791 10.6628 5.46439 10.8498 5.84898 10.696C6.23357 10.5422 6.42063 10.1057 6.26679 9.72109L5.5605 7.95536L9.85289 6.49702Z"
            fill="currentColor"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 relative z-10">No competitors added yet</h3>
      <p className="text-gray-500 dark:text-gray-400 text-center max-w-md mb-6 relative z-10">
        There are no competitors tracked for this product yet. Discover potential competitors by using our search feature.
      </p>
      <div className="flex flex-col gap-4 relative z-10">
        <Button 
          onClick={onSearchClick} 
          disabled={isSearching}
          className="bg-[#FF4A8E] hover:bg-[#ff2c7d] text-white"
        >
          {isSearching ? 'Searching...' : 'Scan for Competitors'}
        </Button>
        <Button 
          onClick={onAddClick}
          variant="outline"
        >
          Add Manually
        </Button>
      </div>
    </div>
  )
}

const Competitors = () => {
  const { competitors, setCompetitors, products, setProducts, selectedProduct, setSelectedProduct } = useContext(DashboardContext)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedCompetitor, setSelectedCompetitor] = useState<Competitor | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<Partial<any>>({})
  const [expandedSegments, setExpandedSegments] = useState<string[]>([])
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [filteredCompetitors, setFilteredCompetitors] = useState<Competitor[]>(competitors)
  const [isCreatingNewSegment, setIsCreatingNewSegment] = useState(false)
  const [newSegmentName, setNewSegmentName] = useState('')
  const productsByKey = useMemo(() => {
    return products.reduce((acc, product) => {
      acc[product.id] = product
      return acc
    }, {})
  }, [products])
  const customFetch = useCustomFetch()

  useEffect(() => {
    if (selectedProduct) {
      setFilteredCompetitors(competitors.filter(competitor => competitor.product_id === selectedProduct.id))
    } else {
      setFilteredCompetitors(competitors)
    }
  }, [selectedProduct, competitors])

  useEffect(() => {
    async function getCompetitorsLogos() {
      const newCompetitors = []
      for (const competitor of competitors) {
        if (!competitor.logo_url) {
          competitor.logo_url = await getLogoUrl(competitor.website_url)
        }
        newCompetitors.push(competitor)
      }
      setCompetitors(newCompetitors)
    }

    getCompetitorsLogos()
  }, [selectedProduct.id])
  
  useEffect(() => {
    setExpandedSegments(selectedProduct.segments || [])
  }, [selectedProduct.id])


  const getLogoUrl = (websiteUrl: string) => {
    try {
      // Extract domain from URL
      const domain = websiteUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
      const baseUrl = 'https://img.logo.dev'
      const token = import.meta.env.VITE_LOGO_DEV_TOKEN
      return `${baseUrl}/${domain}?token=${token}`
    } catch (error) {
      console.error('Error generating logo URL:', error)
      return ''
    }
  }

  // Only show this tab for specific workspace
  // if (activeWorkspace?.id !== 'G7RGUFgtorjsGSW0IB80') {
  //   return null
  // }

  const getThreatLevelColor = (level: number) => {
    if (level >= 8) return 'bg-red-100 text-red-800 border-red-200'
    if (level >= 6) return 'bg-orange-100 text-orange-800 border-orange-200'
    if (level >= 4) return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    return 'bg-green-100 text-green-800 border-green-200'
  }

  const getSimilarityColor = (similarity: number) => {
    if (similarity >= 0.8) return 'bg-red-100 text-red-800 border-red-200'
    if (similarity >= 0.6) return 'bg-orange-100 text-orange-800 border-orange-200'
    if (similarity >= 0.4) return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    return 'bg-green-100 text-green-800 border-green-200'
  }

  const handleEditCompetitor = (competitor: Competitor) => {
    setFormData(competitor)
    setSelectedCompetitor(competitor)
    setIsEditing(true)
  }

  const handleDeleteCompetitor = (competitorId: string) => {
    setSelectedCompetitor(competitors.find(c => c.id === competitorId) || null)
    setIsDeleteDialogOpen(true)
  }

  const confirmDeleteCompetitor = async (competitorId: string) => {
    try {
      await customFetch(`/deleteCompetitor/${competitorId}`, 'DELETE')
      setCompetitors(competitors.filter(c => c.id !== competitorId))
      toast.success('Competitor deleted successfully')
    } catch (error) {
      toast.error('Failed to delete competitor')
    } finally {
      setIsDeleteDialogOpen(false)
    }
  }

  const handleCreateCompetitor = async () => {
    try {
      if (!formData.name) {
        toast.error('Name is required')
        return
      }
      if (!formData.website_url) {
        toast.error('Website URL is required')
        return
      }
      if (!isValidUrl(formData.website_url)) {
        toast.error('Website URL is invalid')
        return
      }
      if (formData.blog_url && !isValidUrl(formData.blog_url)) {
        toast.error('Blog URL is invalid')
        return
      }
      if (formData.twitter_url && !isValidUrl(formData.twitter_url)) {
        toast.error('Twitter URL is invalid')
        return
      }
      if (!formData.segment) {
        toast.error('Product segment is required')
        return
      }
      if (!formData.product_id) {
        toast.error('Product is required')
        return
      }
      const newCompetitor: any = {
        name: formData.name || '',
        websiteUrl: formData.website_url || '',
        threatLevel: formData.threat_level || 0,
        similarity: formData.similarity || 0,
        segment: formData.segment || 1,
        blogUrl: formData.blog_url || '',
        twitterUrl: formData.twitter_url || '',
        productId: formData.product_id || '',
        description: formData.description || '',
        logoUrl: formData.logo_url || ''
      }
      const newCompetitorRes = await customFetch('/createCompetitor', 'POST', newCompetitor)
      setCompetitors([...competitors, newCompetitorRes.competitor])
      setIsAddDialogOpen(false)
      toast.success('Competitor created successfully')
    } catch (error) {
      console.log('error in createCompetitor', error)
      toast.error('Failed to create competitor')
    }
  }

  const handleSaveCompetitor = async () => {
    try {
      if (!formData.name) {
        toast.error('Name is required')
        return
      }
      if (!formData.website_url) {
        toast.error('Website URL is required')
        return
      }
      if (!isValidUrl(formData.website_url)) {
        toast.error('Website URL is invalid')
        return
      }
      if (formData.blog_url && !isValidUrl(formData.blog_url)) {
        toast.error('Blog URL is invalid')
        return
      }
      if (formData.twitter_url && !isValidUrl(formData.twitter_url)) {
        toast.error('Twitter URL is invalid')
        return
      }
      if (!formData.segment) {
        toast.error('Product segment is required')
        return
      }
      if (!formData.product_id) {
        toast.error('Product is required')
        return
      }

      const updateCompetitor: any = {
        competitorId: selectedCompetitor?.id,
        name: formData.name || '',
        websiteUrl: formData.website_url || '',
        threatLevel: formData.threat_level || 0,
        similarity: formData.similarity || 0,
        segment: formData.segment || 1,
        blogUrl: formData.blog_url || '',
        twitterUrl: formData.twitter_url || '',
        productId: formData.product_id || '',
        description: formData.description || '',
        logoUrl: formData.logo_url || ''
      }

      const updatedCompetitor = await customFetch('/updateCompetitor', 'PUT', updateCompetitor)
      setCompetitors(competitors.map(c =>
        c.id === selectedCompetitor?.id ? updatedCompetitor.competitor : c
      ))
      setIsEditing(false)
      toast.success('Competitor updated successfully')
    } catch (error) {
      toast.error('Failed to update competitor')
    }
  }

  const resetForm = () => {
    setFormData({})
    setSelectedCompetitor(null)
  }

  const toggleSegment = (segment: string) => {
    setExpandedSegments(prev => 
      prev.includes(segment) 
        ? prev.filter(id => id !== segment)
        : [...prev, segment]
    )
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      try {
        setIsUploading(true)
        setUploadProgress(0)
        setLogoFile(file)
        const reader = new FileReader()
        reader.onloadend = () => {
          setLogoPreview(reader.result as string)
        }
        reader.readAsDataURL(file)

        // Upload the logo to Firebase Storage
        const logoUrl = await uploadLogo(file, (progress) => {
          setUploadProgress(progress)
        })
        setFormData(prev => ({
          ...prev,
          logo_url: logoUrl
        }))
        toast.success('Logo uploaded successfully')
      } catch (error) {
        console.error('Error uploading logo:', error)
        toast.error('Failed to upload logo')
      } finally {
        setIsUploading(false)
      }
    }
  }

  const handleToggleProduct = (productIds: string[]) => {
    setFormData({
      ...formData,
      product_ids: productIds
    })
  }

  const searchNewCompetitors = async () => {
    try {
      console.log('searching new competitors')
      setSelectedProduct(prev => ({
        ...prev,
        searching_competitors: true
      }))
      setProducts(products.map(product =>
        product.id === selectedProduct.id ? { ...product, searching_competitors: true } : product
      ))
      toast.success('Looking for new competitors... You can reload the page in a few minutes to see the new competitors', {
        duration: 5000,
      })
      await customFetch(`/searchCompetitors/${selectedProduct.id}`, 'GET')
      console.log('competitors searched')
    } catch (error) {
      console.error('Error searching competitors:', error)
      toast.error('Failed to search competitors')
    }
  }

  const handleSelectedProductChange = (value: string) => {
    setSelectedProduct(products.find(product => product.id === value) || {})
  }

  const handleCreateNewSegment = async () => {
    if (!newSegmentName.trim()) {
      toast.error('Segment name is required')
      return
    }

    try {
      // Update the product with the new segment
      const updatedProduct = {
        ...selectedProduct,
        segments: [...(selectedProduct.segments || []), newSegmentName.trim()]
      }
      
      // Update the product in the backend
      await customFetch(`/addProductSegment`, 'POST', {
        id: selectedProduct.id,
        segment: newSegmentName.trim()
      })

      // Update local state
      setSelectedProduct(updatedProduct)
      setProducts(products.map(p => 
        p.id === selectedProduct.id ? updatedProduct : p
      ))
      
      // Set the new segment as the selected segment
      setFormData(prev => ({ ...prev, segment: newSegmentName.trim() }))
      
      // Reset the creation state
      setIsCreatingNewSegment(false)
      setNewSegmentName('')
      
      toast.success('New segment created successfully')
    } catch (error) {
      console.error('Error creating new segment:', error)
      toast.error('Failed to create new segment')
    }
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Competitors</h1>
        <div className="flex gap-2">
          <Select
            value={selectedProduct.id}
            onValueChange={handleSelectedProductChange}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by product" />
            </SelectTrigger>
            <SelectContent>
              {products.map((product) => (
                <SelectItem key={product.id} value={product.id}>
                  {product.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setIsAddDialogOpen(true)}>Add Competitor</Button>
          <Button disabled={selectedProduct.searching_competitors} variant="outline" onClick={searchNewCompetitors}>Search new competitors</Button>
        </div>
      </div>

      {filteredCompetitors.length === 0 ? (
        <EmptyState 
          onSearchClick={searchNewCompetitors} 
          isSearching={selectedProduct.searching_competitors}
          onAddClick={() => setIsAddDialogOpen(true)}
        />
      ) : (
        <div className="space-y-4">
          {Array.isArray(selectedProduct.segments) && selectedProduct.segments.map(segment => {
            const segmentCompetitors = filteredCompetitors.filter(c => c.segment === segment)
            if (segmentCompetitors.length === 0) return null

            return (
              <div key={segment} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => toggleSegment(segment)}
                  className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{segment}</div>
                    <div className="ml-2 text-sm text-gray-500 dark:text-gray-400">({segmentCompetitors.length})</div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform ${
                      expandedSegments.includes(segment) ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedSegments.includes(segment) && (
                  <div className="p-4">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Product</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Threat Level</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Similarity</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Links</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {segmentCompetitors.map((competitor) => (
                          <tr key={competitor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-10 w-10 flex-shrink-0">
                                  <img
                                    className="h-10 w-10 rounded-full object-cover"
                                    src={competitor.logo_url}
                                    alt={competitor.name}
                                  />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{competitor.name}</div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">{competitor.website_url}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 dark:text-gray-100 max-w-md">{competitor.description}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-2">
                                  <span
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100"
                                  >
                                    {productsByKey[competitor.product_id].name}
                                  </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getThreatLevelColor(competitor.threat_level)}`}>
                                {competitor.threat_level}/10
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSimilarityColor(competitor.similarity)}`}>
                                {(competitor.similarity * 100).toFixed(0)}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex space-x-2">
                                <a href={competitor.website_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400">
                                  <ExternalLink size={16} />
                                </a>
                                <a href={competitor.blog_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400">
                                  <BookOpen size={16} />
                                </a>
                                <a href={competitor.twitter_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400">
                                  <Twitter size={16} />
                                </a>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex space-x-2">
                                <Button onClick={() => handleEditCompetitor(competitor)} size="sm" variant="ghost">
                                  <Pencil size={16} />
                                </Button>
                                <Button onClick={() => handleDeleteCompetitor(competitor.id)} size="sm" variant="ghost">
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isAddDialogOpen || isEditing} onOpenChange={(open) => {
        if (!open) {
          setIsAddDialogOpen(false)
          setIsEditing(false)
          resetForm()
          setLogoFile(null)
          setLogoPreview(null)
          setUploadProgress(0)
          setIsUploading(false)
        }
      }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{isAddDialogOpen ? 'Add Competitor' : 'Edit Competitor'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-6 py-4">
            {/* Main Content */}
            <div className="col-span-2 space-y-6">
              {/* Logo Upload */}
              <div className="space-y-2">
                <div className="flex items-center space-x-4">
                  <div className="h-20 w-20 rounded-full overflow-hidden border border-gray-200">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="h-full w-full object-cover" />
                    ) : formData.logo_url ? (
                      <img 
                        src={formData.logo_url} 
                        alt="Logo"
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          const parent = target.parentElement
                          if (parent) {
                            const placeholder = document.createElement('div')
                            placeholder.className = 'h-full w-full bg-gray-100 flex items-center justify-center'
                            placeholder.innerHTML = '<span class="text-gray-400">No logo</span>'
                            parent.appendChild(placeholder)
                          }
                        }}
                      />
                    ) : (
                      <div className="h-full w-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400">No logo</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-violet-50 file:text-violet-700
                          hover:file:bg-violet-100"
                      />
                      {logoPreview && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setLogoPreview(null)
                            setLogoFile(null)
                            setFormData(prev => ({ ...prev, logo_url: '' }))
                          }}
                          className="shrink-0"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    {isUploading && (
                      <div className="mt-2">
                        <Progress value={uploadProgress} className="h-2" />
                        <p className="text-xs text-gray-500 mt-1">
                          Uploading... {Math.round(uploadProgress)}%
                        </p>
                      </div>
                    )}
                    {formData.logo_url && !logoPreview && (
                      <p className="mt-1 text-sm text-gray-500">
                        Using logo from logo.dev. Upload a custom logo to override.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter competitor name"
                  className="text-lg font-medium"
                />
              </div>

              {/* Product Segment */}
              <div>
                <Select
                  value={formData.segment}
                  onValueChange={(value) => {
                    if (value !== 'create-new') {
                      setFormData({ ...formData, segment: value })
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select product segment" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(selectedProduct.segments) && selectedProduct.segments.map((segment) => (
                      <SelectItem key={segment} value={segment}>
                        {segment}
                      </SelectItem>
                    ))}
                    {isCreatingNewSegment ? (
                      <div className="px-2 py-1.5">
                        <div className="flex gap-2">
                          <Input
                            value={newSegmentName}
                            onChange={(e) => setNewSegmentName(e.target.value)}
                            placeholder="Enter new segment name"
                            className="flex-1 h-8"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                handleCreateNewSegment()
                              } else if (e.key === 'Escape') {
                                e.preventDefault()
                                setIsCreatingNewSegment(false)
                                setNewSegmentName('')
                              }
                            }}
                          />
                          <Button 
                            onClick={handleCreateNewSegment}
                            className="shrink-0 h-8"
                            size="sm"
                          >
                            Add
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => {
                              setIsCreatingNewSegment(false)
                              setNewSegmentName('')
                            }}
                            className="shrink-0 h-8"
                            size="sm"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none text-[#FF4A8E] hover:bg-accent hover:text-accent-foreground"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setIsCreatingNewSegment(true)
                        }}
                      >
                        + Add new segment
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter competitor description"
                  rows={4}
                  className="resize-none"
                />
              </div>

              {/* Products */}
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Select
                    value={formData.product_id || ''}
                    onValueChange={(value) => setFormData({ ...formData, product_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a product..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-span-1 space-y-8 border-l pl-6">
              <div className="space-y-6">
                <h3 className="font-semibold text-lg">Links</h3>
                
                {/* Website URL */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-600">
                    <ExternalLink className="w-4 h-4" />
                    Website
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.website_url || ''}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      placeholder="https://example.com"
                      className="flex-1"
                    />
                    {formData.website_url && (
                      <a
                        href={formData.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-500 hover:text-gray-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Blog URL */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-600">
                    <BookOpen className="w-4 h-4" />
                    Blog
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.blog_url || ''}
                      onChange={(e) => setFormData({ ...formData, blog_url: e.target.value })}
                      placeholder="https://blog.example.com"
                      className="flex-1"
                    />
                    {formData.blog_url && (
                      <a
                        href={formData.blog_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-500 hover:text-gray-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Twitter URL */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-600">
                    <Twitter className="w-4 h-4" />
                    Twitter
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.twitter_url || ''}
                      onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                      placeholder="https://twitter.com/example"
                      className="flex-1"
                    />
                    {formData.twitter_url && (
                      <a
                        href={formData.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-500 hover:text-gray-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t">
                <h3 className="font-semibold text-lg mb-6">Metrics</h3>

                {/* Threat Level Slider */}
                <div className="space-y-4">
                  <Label>Threat Level: {formData.threat_level || 0}/10</Label>
                  <Slider
                    value={[formData.threat_level || 0]}
                    onValueChange={(value) => setFormData({ ...formData, threat_level: value[0] })}
                    max={10}
                    step={1}
                    className="w-full [&_[role=slider]]:bg-[#FF4A8E] [&_[role=slider]]:border-[#FF4A8E] [&_[role=slider]]:hover:bg-[#FF4A8E] [&_[role=slider]]:focus:ring-[#FF4A8E] [&_[role=slider]]:focus:ring-offset-[#FF4A8E] [&_[role=slider]]:active:bg-[#FF4A8E]"
                  />
                </div>

                {/* Similarity Slider */}
                <div className="space-y-4">
                  <Label>Similarity: {((formData.similarity || 0) * 100).toFixed(0)}%</Label>
                  <Slider
                    value={[formData.similarity || 0]}
                    onValueChange={(value) => setFormData({ ...formData, similarity: value[0] })}
                    max={1}
                    step={0.1}
                    className="w-full [&_[role=slider]]:bg-[#FF4A8E] [&_[role=slider]]:border-[#FF4A8E] [&_[role=slider]]:hover:bg-[#FF4A8E] [&_[role=slider]]:focus:ring-[#FF4A8E] [&_[role=slider]]:focus:ring-offset-[#FF4A8E] [&_[role=slider]]:active:bg-[#FF4A8E]"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsAddDialogOpen(false)
              setIsEditing(false)
              resetForm()
              setLogoFile(null)
              setLogoPreview(null)
              setUploadProgress(0)
              setIsUploading(false)
            }}>
              Cancel
            </Button>
            <Button onClick={isAddDialogOpen ? handleCreateCompetitor : handleSaveCompetitor}>
              {isAddDialogOpen ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Competitor</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete this competitor? This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => selectedCompetitor && confirmDeleteCompetitor(selectedCompetitor.id)}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Competitors 